import React, {
  forwardRef,
  memo,
  useCallback,
  useImperativeHandle,
  useMemo,
  useState,
} from "react";
import { Box, HStack, Text, Stack, VStack, Spacer } from "@chakra-ui/react";
import colors from "app/colors";
import useMessageThreadDraft from "modules/hooks/use-message-thread-draft";
import { InfoIcon } from "modules/icons";
import { useFile } from "modules/providers/file-provider";
import useCreateMessage from "modules/hooks/use-create-message";
import useMessageThreadCache from "modules/hooks/use-message-thread-cache";
import { useOverlay } from "modules/providers/overlay-provider/overlay-provider";
import { MessageThreadType } from "gql/graphql";
import ComposeBottomToolbar from "./compose-bottom-toolbar";
import useQuill from "modules/quill/use-quill";
import ComposeQuillToolbar from "./quill-toolbar/compose-quill-toolbar";
import ComposeAccessControlToolbar from "./compose-access-control-toolbar";
import useUserPreference from "modules/hooks/use-user-preference";
import { useAuth } from "modules/providers/auth-provider";
import QuillEditor from "./quill-editor";
import ComposeQuotedMessageList from "./compose-quoted-message-list";
import ComposeAttachmentList from "./compose-attachment-list";
import { QuillMention } from "modules/quill/mentions/use-quill-mentions";
import ComposeResizeInputButton from "./compose-resize-input-button";

export interface IQuillEditorHandle {
  appendMention: (mention: QuillMention) => void;
  appendText: (value: string) => void;
  focus: () => void;
  reset: () => void;
}

interface IProps {
  draftId: string;
  type?: MessageThreadType;
  expandOnInit?: boolean;
  autoFocus?: boolean;
  onMessageThreadCreated?: () => void;
}

const QuillMessageComposer = forwardRef<IQuillEditorHandle, IProps>(
  (
    {
      draftId,
      type = MessageThreadType.Thread,
      expandOnInit = false,
      autoFocus = false,
      onMessageThreadCreated,
    },
    ref,
  ) => {
    const { me } = useAuth();
    const { removeLocalFiles, uploadFiles } = useFile();
    const { closeDrawer } = useOverlay();
    const { updateMessageThreadQueryCache } = useMessageThreadCache();
    const { stringValue: enterKeySetting } = useUserPreference({
      userId: me?.id,
      name: "ENTER_KEY_SETTING",
    });

    const [isExpanded, setIsExpanded] = useState(expandOnInit);

    const {
      draft,
      isDraftLoaded,
      error,
      setError,
      files,
      deleteDraft,
      updateMessage,
      canSendMessage,
    } = useMessageThreadDraft({ draftId });

    const isError = useMemo(() => !!error, [error]);

    const placeholder = useMemo(() => {
      switch (draft?.type) {
        case MessageThreadType.Thread:
          return "Click # to add tags or click @ to callout a person or a group";
        case MessageThreadType.Chat:
          return "Type Message";
        case MessageThreadType.Task:
          return "Add task description";
        default:
          return "Type a message";
      }
    }, [draft?.type]);

    const onChange = useCallback(
      (delta: string, plainText: string) => {
        setError(undefined);
        updateMessage(delta, plainText);
      },
      [setError, updateMessage],
    );

    const onFilesAttached = useCallback(
      (files: File[]) => {
        uploadFiles(draftId, files);
      },
      [draftId, uploadFiles],
    );

    const {
      createMessageThread,
      getMessageInputFromDraft,
      getMessageThreadInputFromDraft,
      navigateToNewThread,
      isSending,
    } = useCreateMessage(draftId);

    const onSendMessage = useCallback(async () => {
      if (!draft) return;
      if (!canSendMessage || isSending) {
        return;
      }
      const messageThreadInput = getMessageThreadInputFromDraft(draft);
      const messageInput = getMessageInputFromDraft(draft, files);
      const messageThread = await createMessageThread(
        messageThreadInput,
        messageInput,
        files,
      );
      updateMessageThreadQueryCache(messageThread);
      removeLocalFiles(messageInput.fileList?.map((file) => file.id) || []);
      deleteDraft();
      closeDrawer();
      navigateToNewThread(messageThread);
      onMessageThreadCreated?.();
    }, [
      draft,
      canSendMessage,
      isSending,
      getMessageThreadInputFromDraft,
      getMessageInputFromDraft,
      files,
      createMessageThread,
      updateMessageThreadQueryCache,
      removeLocalFiles,
      deleteDraft,
      closeDrawer,
      navigateToNewThread,
      onMessageThreadCreated,
    ]);

    const onEnterKeyPressed = useCallback(() => {
      if (enterKeySetting !== "send-message") {
        return;
      }
      onSendMessage();
    }, [enterKeySetting, onSendMessage]);

    const {
      containerRef,
      toolbarRef,
      appendText,
      appendMention,
      format,
      insertLink,
      focus,
      reset,
      activeFormats,
      clickedMention,
      closeClickedMention,
    } = useQuill({
      placeholder,
      onChange,
      defaultValue: draft?.delta,
      isReady: isDraftLoaded,
      autoFocus,
      onEnter:
        enterKeySetting === "send-message" ? onEnterKeyPressed : undefined,
      onCtrlEnter: onSendMessage,
      onFilePaste: onFilesAttached,
      type,
    });

    useImperativeHandle(
      ref,
      () => ({
        appendMention,
        appendText,
        focus,
        reset,
      }),
      [appendMention, appendText, focus, reset],
    );

    const borderColor = useMemo(() => {
      if (isError) {
        return colors.red;
      }
      return colors.grayPrimary;
    }, [isError]);

    const focusWithinOutlineColor = useMemo(() => {
      if (isError) {
        return colors.red;
      }
      return colors.blue;
    }, [isError]);

    if (!draft || !isDraftLoaded) return null;

    return (
      <Box
        boxShadow="0px -4px 4px rgba(0, 0, 0, 0.05)"
        w="full"
        minH={100}
        pb={3}
        cursor="text"
        flex={isExpanded ? 1 : undefined}
        display="flex"
        flexDirection="column"
        overflow="hidden"
        mt={isExpanded ? undefined : "auto"}
      >
        <Stack px={3} pt={1} flex={1} overflow="hidden" minH={0}>
          {error && (
            <HStack bg="rgba(233, 69, 69, 0.05)" px={2} py={1} rounded={8}>
              <InfoIcon boxSize={6} fill={colors.red} />
              <Text fontSize="sm" color={colors.red}>
                {error.message}
              </Text>
            </HStack>
          )}
          <HStack w="full">
            <ComposeQuillToolbar
              ref={toolbarRef}
              format={format}
              insertLink={insertLink}
              activeFormats={activeFormats}
            />
            <Spacer />
            <ComposeResizeInputButton
              setIsExpanded={setIsExpanded}
              isExpanded={isExpanded}
            />
          </HStack>
          <VStack
            w="full"
            spacing={0}
            rounded={4}
            borderColor={borderColor}
            borderWidth={1}
            px={2}
            flex={isExpanded ? 1 : undefined}
            minH={0}
            overflow="hidden"
            _focusWithin={{
              outline: "2px solid",
              outlineColor: focusWithinOutlineColor,
            }}
          >
            <ComposeQuotedMessageList draftId={draftId} />
            <QuillEditor
              containerRef={containerRef}
              focus={focus}
              draftId={draftId}
              isExpanded={isExpanded}
              clickedMention={clickedMention}
              onClose={closeClickedMention}
            />
            <ComposeAttachmentList draftId={draftId} />
          </VStack>
          <ComposeAccessControlToolbar draftId={draftId} />
          <ComposeBottomToolbar
            draftId={draftId}
            appendText={appendText}
            appendMention={appendMention}
            onSend={onSendMessage}
            isSending={isSending}
          />
        </Stack>
      </Box>
    );
  },
);

export default memo(QuillMessageComposer);
